<template>
	<div v-if="params.data" class="chip-renderer">
		<div class="chip-renderer__main">
			<span v-if="text !== ''" class="chip-renderer__text">{{ text }}</span>
			<template v-for="(tag, index) in tags" :key="index">
				<!-- An icon-only tag (no text) is a plain decoration, not a chip-worthy label. -->
				<v-icon v-if="!tag.text" class="chip-renderer__icon" :class="tag.class">{{ tag.icon }}</v-icon>
				<v-chip v-else size="x-small" variant="outlined" label :class="['chip-renderer__chip', tag.class]">
					<v-icon v-if="tag.icon">{{ tag.icon }}</v-icon>
					{{ tag.text }}
				</v-chip>
			</template>
			<span v-if="text === '' && !tags.length" class="chip-renderer__empty">—</span>
		</div>
		<span v-if="sub" class="chip-renderer__sub">{{ sub }}</span>
	</div>
</template>

<script setup>
import { computed } from 'vue';

// Generic "text + optional sub-line + trailing chips/icons" cell renderer.
// The caller pre-computes the tag descriptors on the row, so this component owns no business rule.
// cellRendererParams: { textField?, subField?, tagsField? } - textField: null renders chips only.
const props = defineProps({
	params: {
		type: Object,
		required: true,
	},
});

function field(name) {
	const key = props.params?.[name];
	return key ? (props.params.data?.[key] ?? '') : '';
}

// textField explicitly set to null means "chips only"; omitting it falls back to the column's own value.
const text = computed(() => {
	if (props.params?.textField === null) return '';
	if (props.params?.textField) return field('textField');
	return props.params?.value ?? '';
});

const sub = computed(() => field('subField'));

const tags = computed(() => {
	const value = props.params?.tagsField ? props.params.data?.[props.params.tagsField] : null;
	return Array.isArray(value) ? value : [];
});
</script>

<style lang="scss">
.chip-renderer {
	display: flex;
	flex-direction: column;
	justify-content: center;
	height: 100%;
	line-height: 1.25;
	min-width: 0;

	&__main {
		display: flex;
		align-items: center;
		gap: 6px;
		min-width: 0;
		line-height: 24px;
	}

	&__text {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	&__chip {
		flex-shrink: 0;
	}

	&__icon {
		flex-shrink: 0;
		font-size: 20px;
	}

	&__empty {
		color: rgba(var(--v-theme-on-surface), 0.38);
	}

	&__sub {
		font-size: 1rem;
		color: rgba(var(--v-theme-on-surface), 0.6);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}
</style>
